import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { platformLogos } from '../../../constants/platformLogos';

const ConnectedAccountsSection = () => {
  const [accounts, setAccounts] = useState([
    {
      id: 1,
      platform: "Facebook",
      platformId: "facebook",
      username: "",
      connected: false,
      lastSync: null,
      permissions: []
    },
    {
      id: 2,
      platform: "Instagram",
      platformId: "instagram",
      username: "",
      connected: false,
      lastSync: null,
      permissions: []
    },
    {
      id: 3,
      platform: "X",
      platformId: "twitter",
      username: "",
      connected: false,
      lastSync: null,
      permissions: []
    },
    {
      id: 4,
      platform: "LinkedIn",
      platformId: "linkedin",
      username: "",
      connected: false,
      lastSync: null,
      permissions: []
    },
    {
      id: 5,
      platform: "TikTok",
      platformId: "tiktok",
      username: "",
      connected: false,
      lastSync: null,
      permissions: []
    }
  ]);

  const [showPermissions, setShowPermissions] = useState({});

  const handleConnect = (accountId) => {
    setAccounts(prev => prev?.map(acc => 
      acc?.id === accountId 
        ? { ...acc, connected: true, lastSync: new Date()?.toISOString()?.slice(0, 19)?.replace('T', ' ') }
        : acc
    ));
  };

  const handleDisconnect = (accountId) => {
    setAccounts(prev => prev?.map(acc => 
      acc?.id === accountId 
        ? { ...acc, connected: false, lastSync: null }
        : acc
    ));
  };

  const togglePermissions = (accountId) => {
    setShowPermissions(prev => ({
      ...prev,
      [accountId]: !prev?.[accountId]
    }));
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-foreground">Connected Accounts</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your social media platform connections and permissions
        </p>
      </div>
      <div className="space-y-4">
        {accounts?.map((account) => {
          const platformConfig = platformLogos?.[account?.platformId];
          
          return (
            <div 
              key={account?.id}
              className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 p-2"
                    style={{ backgroundColor: `${platformConfig?.color}15` }}
                  >
                    <Image 
                      src={platformConfig?.logo}
                      alt={platformConfig?.alt}
                      className="w-full h-full object-contain"
                      style={{ filter: `drop-shadow(0 0 2px ${platformConfig?.color}40)` }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-foreground">
                        {account?.platform}
                      </h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        account?.connected 
                          ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                      }`}>
                        {account?.connected ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {account?.username}
                    </p>

                    {account?.connected && (
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Clock" size={14} />
                          <span>Last sync: {formatLastSync(account?.lastSync)}</span>
                        </div>
                        <button
                          onClick={() => togglePermissions(account?.id)}
                          className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                        >
                          <Icon name="Shield" size={14} />
                          <span>View permissions</span>
                        </button>
                      </div>
                    )}

                    {showPermissions?.[account?.id] && account?.connected && (
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs font-medium text-foreground mb-2">
                          Granted Permissions:
                        </p>
                        <ul className="space-y-1">
                          {account?.permissions?.map((permission, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Icon name="Check" size={12} className="text-success" />
                              <span>{permission}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  {account?.connected ? (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        iconName="RefreshCw"
                        onClick={() => handleConnect(account?.id)}
                      >
                        Refresh
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDisconnect(account?.id)}
                      >
                        Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button 
                      variant="default" 
                      size="sm"
                      iconName="Link"
                      iconPosition="left"
                      onClick={() => handleConnect(account?.id)}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">
              About Platform Connections
            </p>
            <p className="text-xs text-muted-foreground">
              Connecting your social media accounts allows SocialSeeds to post content, monitor engagement, and analyze performance on your behalf. You can revoke access at any time. We use official OAuth authentication for secure connections.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectedAccountsSection;